import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { collection, query, orderBy, limit, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import {
  Notification, People, Location, Calendar, SearchNormal1, InfoCircle, ArrowRight2, ExportSquare,
} from "iconsax-reactjs";
import {
  FEED_FILTERS, buildFeed, filterFeed, groupByDay, isUnseen, readSeenAt, timeLabel, writeSeenAt,
} from "../utils/notificationFeed";
import "./DealerNotifications.css";

const FEED_LIMIT = 30;

const KIND_ICONS = {
  lead: People,
  visit: Calendar,
  nearby: Location,
  alert: SearchNormal1,
  broadcast: Notification,
  update: InfoCircle,
};

const EMPTY_COPY = {
  all: {
    title: "No notifications yet",
    text: "New leads, visit requests and updates from GharSetu show up here.",
  },
  leads: {
    title: "No lead alerts yet",
    text: "You're alerted here when someone enquires about your listings, asks for a visit, or enquires about a property near your office.",
  },
  updates: {
    title: "No updates from GharSetu yet",
    text: "Announcements from the GharSetu team show up here.",
  },
};

/** The dealer's bell: new-item count, and a panel of lead alerts and GharSetu updates. */
export default function DealerNotifications({ user, onOpenLead }) {
  const uid = user.uid;
  const [personal, setPersonal] = useState(null); // null until the first snapshot
  const [broadcasts, setBroadcasts] = useState(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [seenAt, setSeenAt] = useState(() => readSeenAt(uid));
  // Items after this stay marked new while the panel is open: the marker from
  // before this visit, so opening clears the badge but keeps the markers.
  const [newSince, setNewSince] = useState(seenAt);
  const rootRef = useRef(null);
  const bellRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, "users", uid, "notifications"), orderBy("sentAt", "desc"), limit(FEED_LIMIT));
    return onSnapshot(
      q,
      (snap) => setPersonal(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error("Notifications listener failed:", err);
        setPersonal([]);
        setFailed(true);
      }
    );
  }, [uid]);

  useEffect(() => {
    const q = query(collection(db, "notifications"), orderBy("sentAt", "desc"), limit(FEED_LIMIT));
    return onSnapshot(
      q,
      (snap) => setBroadcasts(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        console.error("Broadcasts listener failed:", err);
        setBroadcasts([]);
        setFailed(true);
      }
    );
  }, []);

  const feed = useMemo(() => buildFeed(personal || [], broadcasts || []), [personal, broadcasts]);
  const loading = personal === null || broadcasts === null;
  const newCount = feed.filter((item) => isUnseen(item, seenAt)).length;

  // "Seen" moves up to the newest item on screen, in server time, so a device
  // clock that runs fast or slow can't leave the badge stuck or swallow what
  // arrives next.
  const markSeen = useCallback(() => {
    const newest = feed[0]?.time || 0;
    if (newest > seenAt) {
      setSeenAt(newest);
      writeSeenAt(uid, newest);
    }
  }, [feed, seenAt, uid]);

  const openPanel = () => {
    setNewSince(seenAt);
    markSeen();
    setOpen(true);
  };

  const closePanel = useCallback((restoreFocus = false) => {
    setOpen(false);
    markSeen();
    if (restoreFocus) bellRef.current?.focus();
  }, [markSeen]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) closePanel();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") closePanel(true);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, closePanel]);

  const activate = (item) => {
    closePanel();
    if (item.lead) onOpenLead(item.lead);
    else if (item.link) window.open(item.link, "_blank", "noopener,noreferrer");
  };

  const renderItem = (item) => {
    const Icon = KIND_ICONS[item.kind] || InfoCircle;
    const isNew = !item.read && item.time > newSince;
    const action = item.lead ? "lead" : item.link ? "link" : null;
    const when = item.time ? new Date(item.time) : null;

    const content = (
      <>
        <span className={`notif-icon${isNew ? " is-new" : ""}`} aria-hidden="true">
          <Icon size={18} variant="Linear" />
        </span>
        <span className="notif-text">
          <span className="notif-item-title">{item.title}</span>
          {item.body && <span className="notif-item-body">{item.body}</span>}
          <span className="notif-meta">
            {isNew && <span className="notif-new">New</span>}
            {when && (
              <time dateTime={when.toISOString()} title={when.toLocaleString()}>
                {timeLabel(item.time)}
              </time>
            )}
            {item.fromGharSetu && <span>From GharSetu</span>}
          </span>
        </span>
        {action === "lead" && (
          <>
            <ArrowRight2 size={16} variant="Linear" className="notif-go" aria-hidden="true" />
            <span className="sr-only">. Opens the lead</span>
          </>
        )}
        {action === "link" && (
          <>
            <ExportSquare size={16} variant="Linear" className="notif-go" aria-hidden="true" />
            <span className="sr-only">. Opens in a new tab</span>
          </>
        )}
      </>
    );

    const className = `notif-item${action ? " is-action" : ""}${isNew ? " is-new" : ""}`;
    return action ? (
      <button type="button" className={className} onClick={() => activate(item)}>
        {content}
      </button>
    ) : (
      <div className={className}>{content}</div>
    );
  };

  const visible = filterFeed(feed, filter);
  const empty = failed && feed.length === 0
    ? { title: "Couldn't load notifications", text: "Check your connection, then open this panel again." }
    : EMPTY_COPY[filter];

  return (
    <div className="notif" ref={rootRef}>
      <button
        ref={bellRef}
        type="button"
        className={`btn-icon notif-bell${open ? " is-open" : ""}`}
        onClick={() => (open ? closePanel() : openPanel())}
        aria-expanded={open}
        aria-controls="notif-panel"
        aria-label={newCount > 0 ? `Notifications, ${newCount} new` : "Notifications"}
      >
        <Notification size={20} variant="Linear" />
        {newCount > 0 && (
          <span className="notif-bell-count" aria-hidden="true">{newCount > 9 ? "9+" : newCount}</span>
        )}
      </button>

      {open && (
        <section id="notif-panel" className="notif-panel" aria-labelledby="notif-title">
          <header className="notif-head">
            <h2 className="notif-title" id="notif-title">Notifications</h2>
            <div className="segmented notif-filter" role="group" aria-label="Show">
              {FEED_FILTERS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={filter === option.id}
                  onClick={() => setFilter(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </header>

          <div className="notif-body">
            {loading ? (
              <ul className="notif-list" aria-busy="true" aria-label="Loading notifications">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="notif-item" aria-hidden="true">
                    <span className="skeleton notif-skel-icon" />
                    <span className="notif-text">
                      <span className="skeleton notif-skel-title" />
                      <span className="skeleton notif-skel-body" />
                    </span>
                  </li>
                ))}
              </ul>
            ) : visible.length === 0 ? (
              <div className="empty-state notif-empty">
                <span className="empty-state-icon" aria-hidden="true">
                  {failed && feed.length === 0
                    ? <InfoCircle size={22} variant="Linear" />
                    : <Notification size={22} variant="Linear" />}
                </span>
                <h3>{empty.title}</h3>
                <p>{empty.text}</p>
              </div>
            ) : (
              groupByDay(visible).map((group) => (
                <div key={group.label} className="notif-group">
                  <h3 className="notif-day">{group.label}</h3>
                  <ul className="notif-list">
                    {group.items.map((item) => <li key={item.key}>{renderItem(item)}</li>)}
                  </ul>
                </div>
              ))
            )}
          </div>
        </section>
      )}
    </div>
  );
}
